import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { updateMyProfile, updateMyVehicle } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Car, Loader2, User, Lock } from "lucide-react";

const BAREME_2024 = {
  voiture: {
    "3": 0.529, "4": 0.606, "5": 0.636, "6": 0.665, "7": 0.697,
  },
  moto: {
    "2": 0.395, "5": 0.468, "6": 0.606,
  },
  scooter: {
    "0": 0.272,
  },
};

const CV_LABELS = {
  voiture: { "3": "3 CV et moins", "4": "4 CV", "5": "5 CV", "6": "6 CV", "7": "7 CV et plus" },
  moto:    { "2": "1 ou 2 CV", "5": "3 à 5 CV", "6": "Plus de 5 CV" },
  scooter: { "0": "≤ 50 cm³" },
};

const roleLabels = {
  collaborator: "Collaborateur",
  manager: "Manager",
  accountant: "Comptable",
  admin: "Administrateur",
};

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingVehicle, setSavingVehicle] = useState(false);

  const [infoForm, setInfoForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [typeVehicule, setTypeVehicule] = useState(user?.vehicle_type || "voiture");
  const [cvFiscal, setCvFiscal] = useState(user?.cv_fiscal || "5");

  const handleVehiculeChange = (v) => {
    setTypeVehicule(v);
    setCvFiscal(v === "voiture" ? "5" : v === "moto" ? "5" : "0");
  };

  const handleInfoSave = async () => {
    setSavingInfo(true);
    try {
      const response = await updateMyProfile({
        first_name: infoForm.first_name,
        last_name: infoForm.last_name,
        email: infoForm.email,
      });
      updateUser(response.data);
      toast.success("Informations mises à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSavingInfo(false);
    }
  };

  const handlePasswordSave = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setSavingPassword(true);
    try {
      await updateMyProfile({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Mot de passe modifié");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleVehicleSave = async () => {
    setSavingVehicle(true);
    try {
      const response = await updateMyVehicle({ vehicle_type: typeVehicule, cv_fiscal: cvFiscal });
      updateUser(response.data);
      toast.success("Véhicule enregistré");
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingVehicle(false);
    }
  };

  const tauxRef = BAREME_2024[typeVehicule]?.[cvFiscal];

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="profile-page">
      <div>
        <h1 className="page-title">Mon profil</h1>
        <p className="page-description">Vos informations et préférences de remboursement</p>
      </div>

      {/* Infos personnelles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" />
            Informations personnelles
          </CardTitle>
          <CardDescription>Votre rôle : <strong>{roleLabels[user?.role] || user?.role}</strong></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>Prénom</Label>
              <Input
                value={infoForm.first_name}
                onChange={(e) => setInfoForm({ ...infoForm, first_name: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Nom</Label>
              <Input
                value={infoForm.last_name}
                onChange={(e) => setInfoForm({ ...infoForm, last_name: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Adresse email</Label>
            <Input
              type="email"
              value={infoForm.email}
              onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })}
            />
          </div>
          <Button
            onClick={handleInfoSave}
            disabled={savingInfo || !infoForm.first_name || !infoForm.last_name || !infoForm.email}
            data-testid="save-info"
          >
            {savingInfo && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enregistrer
          </Button>
        </CardContent>
      </Card>

      {/* Changement de mot de passe */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Changer le mot de passe
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label>Mot de passe actuel</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Nouveau mot de passe</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
            />
          </div>
          <Button
            onClick={handlePasswordSave}
            disabled={savingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
            data-testid="save-password"
          >
            {savingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Changer le mot de passe
          </Button>
        </CardContent>
      </Card>

      {/* Véhicule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Car className="h-5 w-5" />
            Mon véhicule (barème kilométrique)
          </CardTitle>
          <CardDescription>
            Ces paramètres pré-remplissent automatiquement vos notes de frais kilométriques.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type de véhicule</Label>
              <Select value={typeVehicule} onValueChange={handleVehiculeChange}>
                <SelectTrigger data-testid="profile-vehicle-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="voiture">Voiture</SelectItem>
                  <SelectItem value="moto">Moto (&gt; 50 cm³)</SelectItem>
                  <SelectItem value="scooter">Scooter (≤ 50 cm³)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {typeVehicule !== "scooter" && (
              <div className="space-y-2">
                <Label>Puissance fiscale</Label>
                <Select value={cvFiscal} onValueChange={setCvFiscal} data-testid="profile-cv-fiscal">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CV_LABELS[typeVehicule]).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {tauxRef && (
            <div className="rounded-lg bg-muted p-3 text-sm">
              <p className="text-muted-foreground">
                Taux applicable (1ʳᵉ tranche) :{" "}
                <span className="font-semibold text-foreground">{tauxRef.toFixed(3)} €/km</span>
                {" "}— Barème DGFIP 2024
              </p>
            </div>
          )}

          <Button onClick={handleVehicleSave} disabled={savingVehicle} data-testid="save-vehicle">
            {savingVehicle && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enregistrer
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
